import { usePlayerEngine } from "../../../runtime/provider";
import { usePlayer, usePlayerStoreApi } from "@utils/player/core/usePlayer";
import { useEffect, useRef } from "react";
import { useAuthSheet } from "@components/modals/AuthModal/useAuthSheet";
import { AppState, Platform } from "react-native";

import { selectCurrentItem, selectHasHydrated } from "../core/selectors";
import { useAudioPlayerState } from "../audio/useAudioPlayerState";
import { usePlayerStreamAnalytics } from "../features/analytics/usePlayerStreamAnalytics";
import { usePlayerAds } from "../features/ads/usePlayerAds";
import { usePlayerSkipGate } from "../features/limits/usePlayerSkipGate";
import { useForYouBootstrap } from "../features/forYou/useForYouBootstrap";
import {
  shouldLoadForYou,
  isWelcomeStep,
} from "../features/forYou/forYouGating";
import type { OnboardingStep } from "types/auth";
import { useAuth } from "@utils/firebaseAuth/auth";
import { useMe } from "@hooks/trpc/useMe";
import { useListUnlockedPrivateSongs } from "@hooks/trpc/useListUnlockedPrivateSongs";
import type { PlaybackLimitConfig } from "../playbackLimit/types";

import { restorePlaybackResume } from "../features/resume/restorePlaybackResume";

export function usePlayerRuntime(): void {
  const player = usePlayerEngine();
  const { addPlayerBreadcrumb } = player.breadcrumbs;
  const audioPlayer = player.audio;
  const {
    getConfirmedProgressPositionForQueueItem,
    getProgressPositionForQueueItem,
    resetProgressState,
  } = player.progress;

  const playerStore = usePlayerStoreApi();

  const currentItem = usePlayer(selectCurrentItem);
  const hasHydrated = usePlayer(selectHasHydrated);
  const openAuthSheet = useAuthSheet();
  const hasInitializedRef = useRef(false);
  const recordedHydratedQueueWithoutNativeTrackRef = useRef(false);
  const attemptedResumeRestoreRef = useRef(false);

  // Android warm-start fix: Reset playback state when app resumes from background
  // This handles the case where the app is "killed" but JS context survives (warm start)
  useEffect(() => {
    if (Platform.OS !== "android") return;

    // On initial mount, check if we're in a stale "playing" state
    // If userIntent is "playing" but we just mounted, it's a stale state from a warm restart
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      const state = playerStore.getState();

      if (state.userIntent === "playing") {
        // This is a warm restart with stale "playing" state - reset to paused
        // Reset AudioPlayer's internal state to force a full track reload on next play
        // The native player has lost its audio buffer during the warm restart
        console.audio.log(
          "WarmStart: detected stale playing state, resetting",
          {
            activeTrackId: audioPlayer.activeTrackId,
          },
        );
        audioPlayer.resetForWarmStart();

        // Reset progress shared values to clear stale position/duration from UI
        resetProgressState();

        state.actions.setUserIntent("paused");
        state.actions.setStatus("IDLE"); // Use IDLE instead of PAUSED to indicate no track is loaded
      }
    }
  }, []);

  useEffect(() => {
    if (!hasHydrated) return;
    if (recordedHydratedQueueWithoutNativeTrackRef.current) return;
    if (!currentItem) return;
    if (audioPlayer.activeTrackId) return;

    recordedHydratedQueueWithoutNativeTrackRef.current = true;
    addPlayerBreadcrumb(
      "player.hydrated_queue_without_native_track",
      playerStore.getState(),
      {
        songId: currentItem.metadata.id,
        queueItemId: currentItem.qid,
      },
    );
  }, [currentItem, hasHydrated]);

  useEffect(() => {
    if (!hasHydrated || !currentItem) return;
    if (attemptedResumeRestoreRef.current) return;
    attemptedResumeRestoreRef.current = true;

    const state = playerStore.getState();
    restorePlaybackResume({
      resume: player.resume,
      queueType: state.activeQueueType,
      currentItem,
      nativeTrackId: audioPlayer.activeTrackId,
      seek: state.actions.seek,
    })
      .then((result) => {
        if (result.status === "none" || result.status === "native-active") {
          return;
        }
        addPlayerBreadcrumb(
          `player.resume_checkpoint.${result.status}`,
          playerStore.getState(),
          {
            songId: currentItem.metadata.id,
            queueItemId: currentItem.qid,
            positionMs: result.status === "restored" ? result.positionMs : null,
          },
          result.status === "rejected" ? "warning" : "info",
        );
      })
      .catch((error) => {
        addPlayerBreadcrumb(
          "player.resume_checkpoint.failed",
          playerStore.getState(),
          {
            songId: currentItem.metadata.id,
            queueItemId: currentItem.qid,
            error: error instanceof Error ? error.message : String(error),
          },
          "error",
        );
      });
  }, [currentItem, hasHydrated]);

  // Cross-platform: recover playback when app returns to foreground after error
  useEffect(() => {
    let lastRecoveryAtMs = 0;
    const RECOVERY_COOLDOWN_MS = 30_000; // Don't retry more than once per 30s

    const subscription = AppState.addEventListener("change", (nextAppState) => {
      if (nextAppState !== "active") return;

      const state = playerStore.getState();
      const audioState = audioPlayer.getState();

      // If native is loading/buffering/idle, don't interfere
      // "idle" is a legitimate transient state during track transitions
      if (
        audioState.playbackState === "loading" ||
        audioState.playbackState === "idle"
      ) {
        return;
      }

      // If in error state and user intended to play, attempt recovery (with cooldown)
      if (
        audioState.playbackState === "error" &&
        state.userIntent === "playing"
      ) {
        const now = Date.now();
        if (now - lastRecoveryAtMs < RECOVERY_COOLDOWN_MS) {
          console.audio.log(
            "AppState/active: recovery on cooldown, surrendering",
          );
          state.actions.setUserIntent("paused");
          state.actions.setStatus("PAUSED");
          return;
        }
        lastRecoveryAtMs = now;

        const recoveryItem = selectCurrentItem(state);
        const savedPositionMs =
          (getProgressPositionForQueueItem(recoveryItem?.qid) ?? 0) * 1000;
        console.audio.log("AppState/active: attempting error recovery", {
          trackId: audioPlayer.activeTrackId,
          savedPositionMs,
          lastError: audioState.lastError,
        });
        state.actions.playWithStartTime(savedPositionMs).catch((err) => {
          console.audio.error("AppState/active: recovery failed", err);
        });
        return;
      }

      // If store thinks we're playing but audio engine is paused, reset
      if (
        state.userIntent === "playing" &&
        audioState.playbackState === "paused"
      ) {
        state.actions.setUserIntent("paused");
        state.actions.setStatus("PAUSED");
      }
    });

    return () => subscription.remove();
  }, []);

  // Bootstrap For You — gated by onboarding step
  const { firebaseUser } = useAuth();
  const { query: meQuery } = useMe();
  const onboardingStep = meQuery.data?.onboardingStep as
    OnboardingStep | undefined;
  const forYouEnabled = shouldLoadForYou(!!firebaseUser, onboardingStep);
  const importWelcomePending = onboardingStep === "welcome_import_pending";

  useForYouBootstrap(
    forYouEnabled && !importWelcomePending,
    firebaseUser?.uid ?? null,
  );

  // Import welcome is driven by recommendation readiness, not auth changes.
  // The first authed ForYou fetch can legitimately return empty while the
  // import-specific recommendation row is still being written.
  useEffect(() => {
    if (!firebaseUser || !importWelcomePending) {
      return;
    }

    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const delaysMs = [0, 1_000, 2_000, 4_000, 8_000, 10_000, 10_000];

    const loadStandardFallback = () => {
      playerStore
        .getState()
        .actions.loadForYouInitial({
          forceReplace: true,
          resetCursors: true,
          userId: firebaseUser.uid,
        })
        .catch((error) => {
          console.audio.warn(
            "Import welcome fallback ForYou load failed",
            error,
          );
        });
    };

    const load = (attempt: number) => {
      timeout = setTimeout(() => {
        if (cancelled) return;

        playerStore
          .getState()
          .actions.loadForYouInitial({
            forceReplace: true,
            resetCursors: true,
            suppressGeneration: true,
            importOnly: true,
            userId: firebaseUser.uid,
          })
          .then(({ count }) => {
            if (cancelled || count > 0) return;
            if (attempt >= delaysMs.length - 1) {
              loadStandardFallback();
              return;
            }
            const nextAttempt = attempt + 1;
            load(nextAttempt);
          })
          .catch((error) => {
            console.audio.warn(
              "Import welcome ForYou load failed, retrying",
              error,
            );
            if (cancelled) return;
            if (attempt >= delaysMs.length - 1) {
              loadStandardFallback();
              return;
            }
            const nextAttempt = attempt + 1;
            load(nextAttempt);
          });
      }, delaysMs[attempt]);
    };

    load(0);

    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
    };
  }, [firebaseUser, onboardingStep]);

  // Clear the foryou queue when user is authenticated but onboarding isn't done.
  // We clear immediately AND subscribe to catch in-flight async loads that
  // complete after the initial clear (the bootstrap's loadForYouInitial is async).
  // Important: only null foryouQueue, not the full reset() — the user may have
  // a playlist queue loaded from playing an album during onboarding.
  useEffect(() => {
    if (!firebaseUser || forYouEnabled) return;

    const clearForyouQueue = () => {
      playerStore.setState(
        (s) => {
          s.foryouQueue = null;
        },
        false,
        "forYou/clearForOnboarding",
      );
    };

    if (playerStore.getState().foryouQueue) {
      clearForyouQueue();
    }

    const unsub = playerStore.subscribe((state, prevState) => {
      if (state.foryouQueue && !prevState.foryouQueue) {
        clearForyouQueue();
      }
    });
    return unsub;
  }, [firebaseUser, forYouEnabled]);

  // Analytics, Ads, Skip Gate
  // Suppress normal stream tracking during welcome — backend fires the fake stream
  const suppressStreamAnalytics =
    !!onboardingStep && isWelcomeStep(onboardingStep);
  usePlayerStreamAnalytics({ disabled: suppressStreamAnalytics });
  usePlayerAds();
  usePlayerSkipGate();

  // Observe AudioPlayer state and sync to store
  useAudioPlayerState();

  const song = currentItem?.metadata;
  const unlockedPrivateSongIds = useListUnlockedPrivateSongs();

  // Playback Limit Logic
  let playbackLimitConfig: PlaybackLimitConfig;

  const isUnlocked =
    song?.private && unlockedPrivateSongIds
      ? unlockedPrivateSongIds.includes(song.id)
      : null;

  if (
    song &&
    song.private &&
    (isUnlocked === false || !unlockedPrivateSongIds)
  ) {
    playbackLimitConfig = {
      enabled: true,
      limitSeconds: 10,
      onLimitReached: () => {
        const store = playerStore.getState();
        store.actions.pause().catch(console.error);
        store.actions.seek(0).catch(console.error);
      },
    };
  } else if (!firebaseUser) {
    playbackLimitConfig = {
      enabled: true,
      limitSeconds: 30,
      onLimitReached: () => {
        playerStore.getState().actions.pause().catch(console.error);
        openAuthSheet();
      },
    };
  } else {
    playbackLimitConfig = { enabled: false };
  }

  // Sync playbackLimitConfig to store - only if actually changed
  useEffect(() => {
    const store = playerStore.getState();
    const current = store.playbackLimitConfig;
    // Only update if config actually changed to avoid cascading re-renders
    const currentLimit = current.enabled ? current.limitSeconds : 0;
    const newLimit = playbackLimitConfig.enabled
      ? playbackLimitConfig.limitSeconds
      : 0;
    if (
      current.enabled !== playbackLimitConfig.enabled ||
      currentLimit !== newLimit
    ) {
      store.actions.setPlaybackLimitConfig(playbackLimitConfig);
    }
  }, [playbackLimitConfig]);

  // Reset limit reached on song changes - only if currently true
  useEffect(() => {
    const store = playerStore.getState();
    // Only update if currently true to avoid unnecessary store updates
    if (store.hasReachedPlaybackLimit) {
      store.actions.setHasReachedPlaybackLimit(false);
    }
  }, [currentItem?.qid, playbackLimitConfig.enabled]);

  // Enforce limit - independent of store updates, reads from shared values
  useEffect(() => {
    if (!playbackLimitConfig.enabled) return;

    const checkLimit = () => {
      const position = getConfirmedProgressPositionForQueueItem(
        currentItem?.qid,
      );
      if (position == null) return;
      const hasReached = playerStore.getState().hasReachedPlaybackLimit;
      if (hasReached) return;
      if (position < playbackLimitConfig.limitSeconds) return;

      playerStore.getState().actions.setHasReachedPlaybackLimit(true);
      playbackLimitConfig.onLimitReached();
    };

    const interval = setInterval(checkLimit, 500); // Check 2x/sec
    return () => clearInterval(interval);
  }, [currentItem?.qid, playbackLimitConfig]);
}
